import {
  CONSENT_COOKIE,
  CONSENT_MAX_AGE,
  CONSENT_VERSION,
  FIRST_TOUCH_COOKIE,
  FIRST_TOUCH_MAX_AGE,
  FIRST_TOUCH_PENDING_KEY,
  COOKIE_PARENT_DOMAIN,
  OPEN_EVENT,
} from './config';

const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined';

const onParentDomain = () => {
  const h = window.location.hostname;
  return h === COOKIE_PARENT_DOMAIN || h.endsWith(`.${COOKIE_PARENT_DOMAIN}`);
};

const readCookie = (name) => {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? m[1] : null;
};

const writeCookie = (name, value, maxAge) => {
  let c = `${name}=${value}; Path=/; Max-Age=${maxAge}; SameSite=Lax`;
  if (onParentDomain()) c += `; Domain=.${COOKIE_PARENT_DOMAIN}`;
  if (window.location.protocol === 'https:') c += '; Secure';
  document.cookie = c;
};

// Expire a cookie under every domain it may have been set on: host-only,
// the shared .joinin.ai, and each parent (GA picks the widest it can).
const deleteCookie = (name) => {
  const parts = window.location.hostname.split('.');
  const domains = [''];
  for (let i = 0; i < parts.length - 1; i += 1) domains.push(`.${parts.slice(i).join('.')}`);
  domains.forEach((d) => {
    document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax${d ? `; Domain=${d}` : ''}`;
  });
};

const decodeJson = (raw) => {
  try {
    return JSON.parse(decodeURIComponent(raw));
  } catch (e) {
    return null;
  }
};

/** The visitor's explicit choice: true, false, or null if they have not chosen. */
export const readChoice = () => {
  if (!isBrowser()) return null;
  const raw = readCookie(CONSENT_COOKIE);
  const c = raw && decodeJson(raw);
  return c && c.v === CONSENT_VERSION && typeof c.analytics === 'boolean' ? c.analytics : null;
};

// Google resolves Consent Mode regions server-side from the IP; the page
// cannot see that answer. For our own first-party cookie we estimate the
// region from the time zone and err towards "denied": any Europe/* zone,
// Atlantic/Arctic zones of EEA states, Canadian zones, or an unknown/UTC zone.
const CANADIAN_ZONES = /^America\/(Toronto|Montreal|Vancouver|Edmonton|Winnipeg|Regina|Swift_Current|Halifax|Glace_Bay|Moncton|Goose_Bay|St_Johns|Whitehorse|Dawson|Dawson_Creek|Creston|Fort_Nelson|Yellowknife|Inuvik|Iqaluit|Pangnirtung|Rankin_Inlet|Resolute|Cambridge_Bay|Atikokan|Coral_Harbour|Blanc-Sablon|Nipigon|Thunder_Bay|Rainy_River)$/;
const EUROPEAN_ZONES = /^(Europe\/|Atlantic\/(Reykjavik|Canary|Madeira|Azores|Faroe)$|Arctic\/Longyearbyen$|Africa\/Ceuta$)/;

export const inDefaultDeniedRegion = () => {
  let tz = '';
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (e) {
    return true;
  }
  if (!tz || /^(UTC|Etc\/|GMT)/.test(tz)) return true;
  return EUROPEAN_ZONES.test(tz) || CANADIAN_ZONES.test(tz);
};

/** Explicit choice if any, otherwise the regional default. */
export const analyticsAllowed = () => {
  const choice = readChoice();
  return choice === null ? !inDefaultDeniedRegion() : choice;
};

const clean = (v) => {
  const s = String(v || '').slice(0, 100);
  return s.includes('@') ? '' : s; // never let an email in a UTM tag through
};

const readPendingLanding = () => {
  try {
    const s = window.sessionStorage.getItem(FIRST_TOUCH_PENDING_KEY);
    if (s) return JSON.parse(s);
  } catch (e) {
    // sessionStorage blocked: fall back to the in-memory copy
  }
  return window.__joininLanding || null;
};

const clearPendingLanding = () => {
  try {
    window.sessionStorage.removeItem(FIRST_TOUCH_PENDING_KEY);
  } catch (e) {
    // ignore
  }
  window.__joininLanding = null;
};

/** Write joinin_ft from the held landing data. Never overwrites. */
export const captureFirstTouch = () => {
  if (!isBrowser()) return;
  if (readCookie(FIRST_TOUCH_COOKIE) !== null) {
    clearPendingLanding();
    return;
  }
  const l = readPendingLanding();
  if (!l) return;
  const value = {
    v: 1,
    src: clean(l.src),
    med: clean(l.med),
    cmp: clean(l.cmp),
    cnt: clean(l.cnt),
    ref: clean(l.ref),
    lp: clean(l.lp) || '/',
    ts: typeof l.ts === 'number' ? l.ts : Date.now(),
  };
  writeCookie(FIRST_TOUCH_COOKIE, encodeURIComponent(JSON.stringify(value)), FIRST_TOUCH_MAX_AGE);
  clearPendingLanding();
};

const GA_COOKIE = /^(_ga(_.*)?|_gid|_gat(_.*)?)$/;

const purgeAnalyticsStorage = () => {
  deleteCookie(FIRST_TOUCH_COOKIE);
  clearPendingLanding();
  document.cookie
    .split('; ')
    .map((c) => c.split('=')[0])
    .filter((n) => GA_COOKIE.test(n))
    .forEach(deleteCookie);
};

const updateGtag = (allowed) => {
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', { analytics_storage: allowed ? 'granted' : 'denied' });
  }
};

/** Record an explicit choice, tell Google, and apply it to our own storage. */
export const saveChoice = (analytics) => {
  const value = { v: CONSENT_VERSION, analytics: !!analytics, ts: Date.now() };
  writeCookie(CONSENT_COOKIE, encodeURIComponent(JSON.stringify(value)), CONSENT_MAX_AGE);
  updateGtag(!!analytics);
  if (analytics) captureFirstTouch();
  else purgeAnalyticsStorage();
};

/** Run once per page load (gatsby-browser onClientEntry). */
export const initConsent = () => {
  if (!isBrowser()) return;
  if (readChoice() === false) purgeAnalyticsStorage();
  else if (analyticsAllowed()) captureFirstTouch();
};

export const openCookieSettings = () => {
  if (isBrowser()) window.dispatchEvent(new CustomEvent(OPEN_EVENT));
};

export { OPEN_EVENT };
